import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchForwardPE } from '../../api/QuantApi';
import LineChart from '../BPlusSignal/LineChart';
import styles from '../BPlusSignal/BPlusSignal.module.css';
import own from './ForwardPE.module.css';
import translations from './translations';

const COLORS = {
    fwd: '#3b5bdb',
    fwd_d: '#1f9d72',
    median: '#5c6b7a',
    quartile: '#98a2b3',
};

const RANGE_MONTHS = { '1y': 12, '3y': 36, '5y': 60, '10y': 120, all: null };
const BASELINES = ['5y', '10y', '20y', 'all'];

const readPref = (key, fallback, allowed) => {
    try {
        const v = window.localStorage.getItem(key);
        return allowed.includes(v) ? v : fallback;
    } catch (e) {
        return fallback;
    }
};
const writePref = (key, value) => {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Private mode or blocked storage: the choice just isn't remembered.
    }
};

const pct = (v, digits = 1, sign = false) => {
    if (v == null || !Number.isFinite(v)) return '—';
    const s = `${(v * 100).toFixed(digits)}%`;
    return sign && v > 0 ? `+${s}` : s;
};
const usd = (v) => (v == null || !Number.isFinite(v) ? '—' : `$${v.toFixed(2)}`);
const mult = (v, digits = 1) => (v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(digits)}×`);
const fmt = (str, vars = {}) => String(str).replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));

function Toggle({ options, value, onChange, label }) {
    return (
        <div className={styles.toggle} role="group" aria-label={label}>
            {options.map((o) => (
                <button
                    key={o.id}
                    type="button"
                    className={value === o.id ? styles.toggleActive : ''}
                    aria-pressed={value === o.id}
                    onClick={() => onChange(o.id)}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

// Today's reading on the baseline's low–high scale, with the middle half shaded.
function RangeBar({ w, current, t }) {
    const span = w.max - w.min || 1;
    const pos = (v) => Math.min(100, Math.max(0, ((v - w.min) / span) * 100));
    return (
        <div className={own.range}>
            <div className={own.track}>
                <div className={own.band} style={{ left: `${pos(w.p25)}%`, width: `${pos(w.p75) - pos(w.p25)}%` }} />
                <div className={own.median} style={{ left: `${pos(w.median)}%` }} />
                <div className={own.marker} style={{ left: `${pos(current)}%` }} />
            </div>
            <div className={own.rangeEnds}>
                <span>{t.now.low} {mult(w.min)} · {w.min_date.slice(0, 7)}</span>
                <span>{t.now.high} {mult(w.max)} · {w.max_date.slice(0, 7)}</span>
            </div>
            <div className={own.rangeLegend}>
                <span><i className={own.keyBand} />{t.now.middle} {mult(w.p25)} – {mult(w.p75)}</span>
                <span><i className={own.keyMedian} />{t.now.median} {mult(w.median)}</span>
                <span><i className={own.keyMarker} />{t.now.today} {mult(current, 2)}</span>
            </div>
        </div>
    );
}

function ForwardPE() {
    const [lang, setLangState] = useState(() => readPref(
        'fpe.lang',
        (navigator.language || '').toLowerCase().startsWith('zh') ? 'zh' : 'en',
        ['en', 'zh'],
    ));
    const [baseline, setBaselineState] = useState(() => readPref('fpe.baseline', '10y', BASELINES));
    const [range, setRangeState] = useState(() => readPref('fpe.range', '10y', Object.keys(RANGE_MONTHS)));
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const t = translations[lang];
    const setLang = (v) => { setLangState(v); writePref('fpe.lang', v); };
    const setBaseline = (v) => { setBaselineState(v); writePref('fpe.baseline', v); };
    const setRange = (v) => { setRangeState(v); writePref('fpe.range', v); };

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchForwardPE()
            .then(setData)
            .catch(setError)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const windows = (data && data.windows) || [];
    const w = windows.find((x) => x.key === baseline) || windows.find((x) => x.key === '10y') || windows[0];
    const periodText = w
        ? (w.years ? fmt(t.period.years, { n: w.years }) : fmt(t.period.all, { start: w.start.slice(0, 4) }))
        : '';

    // Visible chart rows, each carrying the baseline's median and quartiles as flat lines.
    const chartRows = useMemo(() => {
        if (!data || !data.chart || !data.chart.length) return [];
        let rows = data.chart;
        const months = RANGE_MONTHS[range];
        if (months) {
            const cut = new Date(rows[rows.length - 1].date);
            cut.setMonth(cut.getMonth() - months);
            const iso = cut.toISOString().slice(0, 10);
            rows = rows.filter((r) => r.date >= iso);
        }
        if (!w) return rows;
        return rows.map((r) => ({ ...r, median: w.median, p25: w.p25, p75: w.p75 }));
    }, [data, range, w]);

    const header = (
        <header className={styles.header}>
            <div>
                <div className={styles.kicker}>{t.kicker}</div>
                <h1 className={styles.title}>{t.title}</h1>
                <p className={styles.subtitle}>{t.subtitle}</p>
                <p className={styles.asOf}>
                    {data && data.available && (
                        <>
                            {fmt(t.asOf, { date: data.as_of, basis: t.basis[data.current.basis] })}
                            <span className={styles.dot}>·</span>
                        </>
                    )}
                    <Link to="/bplus" className={styles.inlineLink}>{t.links.bplus} →</Link>
                    <span className={styles.dot}>·</span>
                    <Link to="/quantmonitor" className={styles.inlineLink}>{t.links.monitor} →</Link>
                </p>
            </div>
            <div className={styles.controls}>
                <div className={styles.controlRow}>
                    <span>{t.baseline}</span>
                    <Toggle label={t.baseline} value={w ? w.key : baseline} onChange={setBaseline}
                        options={BASELINES.filter((k) => !windows.length || windows.some((x) => x.key === k))
                            .map((k) => ({ id: k, label: t.windows[k] }))} />
                </div>
                <div className={styles.controlRow}>
                    <span>{t.language}</span>
                    <Toggle label={t.language} value={lang} onChange={setLang}
                        options={[{ id: 'en', label: 'EN' }, { id: 'zh', label: '中文' }]} />
                </div>
            </div>
        </header>
    );

    const shell = (body) => <div className={styles.page}><div className={styles.inner}>{header}{body}</div></div>;

    if (loading && !data) {
        return shell(<div className={styles.card}><p className={styles.muted}>{t.loading}</p></div>);
    }
    if (error && !data) {
        return shell(
            <div className={styles.card}>
                <p className={styles.errorText}>{t.error}</p>
                <button type="button" className={styles.retry} onClick={load}>{t.retry}</button>
            </div>,
        );
    }
    if (!data || !data.available || !w) {
        return shell(<div className={styles.card}><p className={styles.muted}>{t.unavailable}</p></div>);
    }

    const cur = data.current.pe;
    const eps = data.eps.now;
    const cross = data.cross_check;
    const main = data.sources.main;
    const weeklyEnd = main.weekly_until || data.as_of;

    const hasKey = (k) => chartRows.some((r) => r[k] != null);
    const chartSeries = [
        { key: 'p75', label: t.chart.series.p75, color: COLORS.quartile, width: 1, dash: '3 4' },
        { key: 'median', label: t.chart.series.median, color: COLORS.median, width: 1.2, dash: '6 4' },
        { key: 'p25', label: t.chart.series.p25, color: COLORS.quartile, width: 1, dash: '3 4' },
        ...(hasKey('fwd') ? [{ key: 'fwd', label: t.chart.series.fwd, color: COLORS.fwd, width: 1.8 }] : []),
        ...(hasKey('fwd_d') ? [{ key: 'fwd_d', label: t.chart.series.fwd_d, color: COLORS.fwd_d, width: 2 }] : []),
    ];

    const levelRows = [
        { key: 'max', pe: w.max, label: fmt(t.levels.rows.max, { date: w.max_date.slice(0, 7) }) },
        { key: 'p75', pe: w.p75, label: t.levels.rows.p75 },
        { key: 'median', pe: w.median, label: t.levels.rows.median },
        { key: 'p25', pe: w.p25, label: t.levels.rows.p25 },
        { key: 'min', pe: w.min, label: fmt(t.levels.rows.min, { date: w.min_date.slice(0, 7) }) },
        { key: 'today', pe: cur, label: t.levels.rows.today, strong: true },
    ].sort((a, b) => b.pe - a.pe || (a.strong ? -1 : 1));

    return shell(
        <>
            {/* ---- Today ---- */}
            <section className={`${styles.card} ${styles.signalCard}`} aria-labelledby="fpe-now">
                <div className={styles.signalMain}>
                    <div className={styles.cardLabel} id="fpe-now">{t.now.heading}</div>
                    <div className={own.headline}>
                        <span className={own.bigValue}>{mult(cur, 2)}</span>
                        <span className={`${own.zone} ${own[`zone_${w.zone}`] || ''}`}>
                            {t.zones[w.zone]} · {fmt(t.now.zoneVs, { period: periodText })}
                        </span>
                    </div>
                    <p className={own.desc}>
                        {fmt(t.now.desc, {
                            pct: pct(w.pct_rank, 0), period: periodText,
                            median: mult(w.median), p25: mult(w.p25), p75: mult(w.p75),
                        })}
                    </p>
                    {data.live && (
                        <p className={own.live}>
                            {fmt(t.now.live, { date: data.live.date, price: usd(data.live.close), pe: mult(data.live.pe) })}
                        </p>
                    )}
                    <RangeBar w={w} current={cur} t={t} />
                </div>
                <div className={styles.signalStats}>
                    <div className={styles.stat}>
                        <div className={styles.statLabel}>{t.now.eps}</div>
                        <div className={styles.statValue}>{usd(eps)}</div>
                        {data.eps.change_1y != null && (
                            <div className={data.eps.change_1y >= 0 ? styles.pos : styles.neg}>
                                {fmt(t.now.epsChange, { v: pct(data.eps.change_1y, 1, true) })}
                            </div>
                        )}
                    </div>
                    <div className={styles.stat}>
                        <div className={styles.statLabel}>{t.now.ey}</div>
                        <div className={styles.statValue}>{pct(data.yields.earnings, 2)}</div>
                        {data.yields.tbill != null && (
                            <div className={own.statSub}>{fmt(t.now.tbill, { v: pct(data.yields.tbill, 2) })}</div>
                        )}
                    </div>
                    <div className={styles.stat}>
                        <div className={styles.statLabel}>{fmt(t.now.close, { date: data.qqq.date || '—' })}</div>
                        <div className={styles.statValue}>{usd(data.qqq.close)}</div>
                    </div>
                </div>
            </section>

            {/* ---- Chart ---- */}
            <section className={styles.card} aria-labelledby="fpe-chart">
                <div className={styles.cardHead}>
                    <div>
                        <h2 className={styles.h2} id="fpe-chart">{t.chart.heading}</h2>
                        <p className={styles.note}>{fmt(t.chart.note, { weeklyEnd, period: periodText })}</p>
                    </div>
                    <Toggle label={t.chart.heading} value={range} onChange={setRange}
                        options={Object.keys(RANGE_MONTHS).map((k) => ({ id: k, label: t.chart.ranges[k] }))} />
                </div>
                <div className={styles.legend}>
                    {chartSeries.filter((s) => s.key !== 'p25').map((s) => (
                        <span key={s.key} className={styles.legendItem}>
                            <span className={styles.legendLine} style={{ borderTop: `2px ${s.dash ? 'dashed' : 'solid'} ${s.color}` }} />
                            {s.key === 'p75' ? `${t.chart.series.p25} / ${t.chart.series.p75}` : s.label}
                        </span>
                    ))}
                </div>
                <LineChart
                    data={chartRows}
                    series={chartSeries}
                    timeScale
                    height={360}
                    yFormat={(v, tip) => (tip ? mult(v, 2) : `${Math.round(v)}×`)}
                    xFormat={range === '1y' || range === '3y' ? (d) => d.slice(0, 7) : (d) => d.slice(0, 4)}
                    ariaLabel={t.chart.heading}
                />
            </section>

            <div className={styles.twoCol}>
                {/* ---- Price at other multiples ---- */}
                <section className={styles.card} aria-labelledby="fpe-levels">
                    <h2 className={styles.h2} id="fpe-levels">{t.levels.heading}</h2>
                    <p className={styles.note}>{fmt(t.levels.note, { eps: usd(eps), period: periodText })}</p>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t.levels.level}</th>
                                    <th className={styles.num}>{t.levels.multiple}</th>
                                    <th className={styles.num}>{t.levels.price}</th>
                                    <th className={styles.num}>{t.levels.distance}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {levelRows.map((r) => {
                                    const d = r.pe / cur - 1;
                                    return (
                                        <tr key={r.key} className={r.strong ? styles.rowStrong : ''}>
                                            <td>{r.label}</td>
                                            <td className={styles.num}>{mult(r.pe)}</td>
                                            <td className={styles.num}>{usd(data.qqq.close * (r.pe / cur))}</td>
                                            <td className={`${styles.num} ${r.strong ? '' : d >= 0 ? styles.pos : styles.neg}`}>
                                                {r.strong ? '—' : pct(d, 1, true)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* ---- Against its own history ---- */}
                <section className={styles.card} aria-labelledby="fpe-history">
                    <h2 className={styles.h2} id="fpe-history">{t.history.heading}</h2>
                    <p className={styles.note}>{t.history.note}</p>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t.history.period}</th>
                                    <th className={styles.num}>{t.history.median}</th>
                                    <th className={styles.num}>{t.history.range}</th>
                                    <th className={styles.num}>{t.history.pct}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {windows.map((x) => (
                                    <tr key={x.key} className={x.key === w.key ? styles.rowStrong : ''}>
                                        <td>{fmt(t.history.names[x.key], { start: x.start.slice(0, 4) })}</td>
                                        <td className={styles.num}>{mult(x.median)}</td>
                                        <td className={styles.num}>{mult(x.min)} – {mult(x.max)}</td>
                                        <td className={styles.num}>
                                            {pct(x.pct_rank, 0)}
                                            <span className={own.zoneText}>{t.zones[x.zone]}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <div className={styles.twoCol}>
                {/* ---- Year by year ---- */}
                <section className={styles.card} aria-labelledby="fpe-years">
                    <h2 className={styles.h2} id="fpe-years">{t.years.heading}</h2>
                    <div className={`${styles.tableWrap} ${styles.tall}`}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t.years.year}</th>
                                    <th className={styles.num}>{t.years.end}</th>
                                    <th className={styles.num}>{t.years.high}</th>
                                    <th className={styles.num}>{t.years.low}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.years.map((y) => (
                                    <tr key={y.year}>
                                        <td className={styles.nowrap}>
                                            {y.partial ? fmt(t.years.ytd, { year: y.year }) : y.year}
                                            {y.since && <span className={own.zoneText}>{fmt(t.years.since, { date: y.since })}</span>}
                                        </td>
                                        <td className={styles.num}>{mult(y.end)}</td>
                                        <td className={styles.num}>{mult(y.high)}</td>
                                        <td className={styles.num}>{mult(y.low)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* ---- Cross-check and sources ---- */}
                <section className={styles.card} aria-labelledby="fpe-cross">
                    <h2 className={styles.h2} id="fpe-cross">{t.cross.heading}</h2>
                    <div className={own.sourceName}>
                        <a href={data.sources.wsj.url} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
                            {t.cross.name}
                        </a>
                    </div>
                    {cross ? (
                        <>
                            <div className={styles.statLabel}>{fmt(t.cross.when, { date: cross.date })}</div>
                            <div className={own.crossGrid}>
                                <div className={styles.stat}>
                                    <div className={styles.statLabel}>{t.cross.forward}</div>
                                    <div className={styles.statValue}>{mult(cross.forward, 2)}</div>
                                </div>
                                <div className={styles.stat}>
                                    <div className={styles.statLabel}>{t.cross.trailing}</div>
                                    <div className={styles.statValue}>{mult(cross.trailing, 2)}</div>
                                </div>
                                <div className={styles.stat}>
                                    <div className={styles.statLabel}>{t.cross.growth}</div>
                                    <div className={styles.statValue}>{pct(cross.implied_growth, 0, true)}</div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <p className={styles.muted}>{t.cross.missing}</p>
                    )}
                    <p className={styles.note}>{t.cross.note}</p>

                    <h2 className={styles.h2}>{t.about.heading}</h2>
                    <ul className={own.aboutList}>
                        {t.about.items.map((item) => <li key={item}>{fmt(item, { weeklyEnd })}</li>)}
                    </ul>
                    <p className={own.credit}>
                        {(() => {
                            const [before, after] = t.about.credit.split('{source}');
                            return (
                                <>
                                    {before}
                                    <a href={main.url} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
                                        {main.name} (historyofmarket.com)
                                    </a>
                                    {after}
                                </>
                            );
                        })()}
                    </p>
                </section>
            </div>

            <p className={styles.disclaimer}>{t.disclaimer}</p>
        </>,
    );
}

export default ForwardPE;
